// @ts-check
// apps/crm/dashboard/modules/stripe-key-dialog.js
// ── The window that asks a coach for their own Stripe key ─────────────
//
// Stripe lets only the app that made a subscription, or the account owner,
// change it. When a coach cancels, pauses or changes a client whose
// subscription another app made (GoHighLevel's, say), the server answers
// 409 { needsStripeKey } and nothing has changed. api.js's callFunction opens
// this window; once the key is saved it sends the same request again, so the
// coach's cancel finishes without a second click. Settings → Payments opens
// the same window to add or replace the key.
//
// ⚠️ The pasted key goes straight to the server (stripe-owner-key) and is
// never kept, logged or shown again here; the field is cleared on close.
//
// Imports nothing from api.js on purpose: api.js loads this file, so saving
// arrives as the `save` callback instead of a callFunction import (no cycle).

const STRIPE_KEYS_URL = { live: 'https://dashboard.stripe.com/apikeys', test: 'https://dashboard.stripe.com/test/apikeys' };

/** @type {HTMLElement | null} */
let overlay = null;
/** @type {((saved: boolean) => void) | null} */
let settle = null;

/** @param {string} id */
const el = (id) => /** @type {HTMLElement} */ (document.getElementById(id));

/**
 * A value the coach types into Stripe, with a one-click Copy beside it.
 * The values are this file's own constants, never anything a coach typed.
 * @param {string} value
 */
const copyChip = (value) => `<span style="display:inline-flex;align-items:center;gap:6px;vertical-align:middle;">`
  + `<code style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:var(--fs-xs);padding:2px 6px;border:1px solid var(--border);border-radius:4px;background:var(--surface-2);color:var(--text);">${value}</code>`
  + `<button type="button" class="btn-secondary" data-copy="${value}" style="padding:2px 8px;font-size:var(--fs-xs);line-height:1.4;">Copy</button></span>`;

/** @param {HTMLButtonElement} btn */
async function copyFrom(btn) {
  const value = btn.dataset.copy || '';
  let copied = false;
  try {
    await navigator.clipboard.writeText(value);
    copied = true;
  } catch (e) {
    // No clipboard (an old browser, or permission refused): select the value
    // beside the button so Cmd+C copies it.
    const code = btn.previousElementSibling;
    const sel = window.getSelection();
    if (code && sel) {
      const range = document.createRange();
      range.selectNodeContents(code);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }
  btn.textContent = copied ? 'Copied' : 'Press ⌘C';
  setTimeout(() => { btn.textContent = 'Copy'; }, 1600);
}

function build() {
  overlay = document.createElement('div');
  overlay.className = 'modal-overlay hidden';
  overlay.id = 'stripe-key-overlay';
  // ⚠️ ABOVE THE CONFIRM DIALOGS. Cancel client and Delete client run their
  // request from inside the legacy confirm dialog (.confirm-overlay, z-index
  // 200 to 100000), which stays open until the request settles. At
  // .modal-overlay's own 100 this window would open BEHIND it, invisible, and
  // the cancel would wait for it forever.
  overlay.style.zIndex = '100001';
  overlay.innerHTML = `
    <div class="modal" style="max-width:540px;" role="dialog" aria-modal="true" aria-labelledby="stripe-key-title">
      <div class="modal-header">
        <h2 id="stripe-key-title">Let FitOps Pro manage this</h2>
        <button class="modal-close" id="stripe-key-close" aria-label="Close">&times;</button>
      </div>
      <div style="padding:20px;">
        <p id="stripe-key-why" style="font-size:var(--fs-sm);color:var(--text);line-height:1.55;margin:0;"></p>
        <p style="font-size:var(--fs-sm);color:var(--text-muted);line-height:1.55;margin:10px 0 0;">Add a key from your Stripe account once, and FitOps Pro can manage these for you from now on. The key can only manage subscriptions and invoices.</p>
        <a id="stripe-key-open" class="btn-secondary" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;margin-top:16px;text-decoration:none;">Open Stripe API keys &#8599;</a>
        <ol style="font-size:var(--fs-sm);color:var(--text);line-height:1.7;margin:14px 0 0;padding-left:20px;">
          <li>Click <strong>Create restricted key</strong>.</li>
          <li>Choose <strong>Powering an integration you built</strong>, then click <strong>Continue</strong>.</li>
          <li>Click <strong>Choose your own</strong>. Skip the templates: they switch on dozens of permissions FitOps Pro doesn't need.</li>
          <li>For <strong>Key name</strong>, paste ${copyChip('FitOps Pro')}</li>
          <li>In <strong>Filter resources</strong>, paste ${copyChip('Subscriptions')}</li>
          <li>On the <strong>Subscriptions</strong> row under <strong>Billing</strong>, click <strong>Write</strong>.</li>
          <li>Clear the filter and paste ${copyChip('Invoices')}</li>
          <li>On the <strong>Invoices</strong> row under <strong>Billing</strong>, click <strong>Write</strong>. Leave everything else as None.</li>
          <li>Click <strong>Create key</strong>, then copy the key. It starts with <span id="stripe-key-prefix" style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">rk_live_</span>.</li>
          <li>Paste it below and click <strong>Save</strong>.</li>
        </ol>
        <label for="stripe-key-input" style="font-size:var(--fs-xs);font-weight:600;color:var(--text);text-transform:uppercase;letter-spacing:0.5px;display:block;margin:18px 0 6px;">Restricted key</label>
        <input type="password" id="stripe-key-input" autocomplete="off" spellcheck="false" placeholder="rk_live_…" style="width:100%;padding:10px;border-radius:var(--radius-sm);border:1px solid var(--border);background:var(--surface-2);color:var(--text);font-size:var(--fs-sm);font-family:ui-monospace,SFMono-Regular,Menlo,monospace;" />
        <p id="stripe-key-error" class="hidden" role="alert" style="font-size:var(--fs-xs);color:var(--danger);line-height:1.5;margin:10px 0 0;"></p>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:18px;">
          <button class="btn-secondary" id="stripe-key-cancel">Cancel</button>
          <button class="btn-primary" id="stripe-key-save" disabled>Save</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const input = /** @type {HTMLInputElement} */ (el('stripe-key-input'));
  input.addEventListener('input', () => {
    /** @type {HTMLButtonElement} */ (el('stripe-key-save')).disabled = !input.value.trim();
    showError('');
  });
  overlay.querySelectorAll('[data-copy]').forEach((b) => b.addEventListener('click', () => copyFrom(/** @type {HTMLButtonElement} */ (b))));
  el('stripe-key-close').addEventListener('click', () => close(false));
  el('stripe-key-cancel').addEventListener('click', () => close(false));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(false); });
  overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(false); });
}

/** @param {string} text */
function showError(text) {
  const p = el('stripe-key-error');
  p.textContent = text;
  p.classList.toggle('hidden', !text);
}

/** @param {boolean} saved */
function close(saved) {
  if (!overlay) return;
  overlay.classList.add('hidden');
  /** @type {HTMLInputElement} */ (el('stripe-key-input')).value = '';
  showError('');
  const done = settle;
  settle = null;
  if (done) done(saved);
}

/**
 * Open the window. Resolves true once a key is saved, false if the coach
 * closes it. Only one can be open: a second call closes the first as unsaved.
 *
 * @param {{
 *   appName?: string | null,     // the app that made the subscription; null = made in their own Stripe account
 *   keyRefused?: boolean,        // a key is saved and Stripe turned it away
 *   replacing?: boolean,         // opened from Settings to replace a saved key
 *   mode?: string,               // 'live' | 'test'
 *   save: (key: string) => Promise<any>,
 * }} opts
 * @returns {Promise<boolean>}
 */
export function askForStripeKey(opts) {
  if (!overlay) build();
  if (settle) close(false);
  const mode = opts.mode === 'test' ? 'test' : 'live';

  el('stripe-key-title').textContent = opts.keyRefused || opts.replacing
    ? 'Replace your Stripe key'
    : opts.appName === undefined
      ? 'Add your Stripe key'
      : 'Let FitOps Pro manage this';
  el('stripe-key-why').textContent = opts.keyRefused
    ? 'Your saved Stripe key did not work for this. Make a new one and paste it below.'
    : opts.replacing
      ? 'Replace the Stripe key FitOps Pro uses for subscriptions and invoices that were made outside FitOps Pro.'
      : opts.appName
        ? `This was set up by ${opts.appName}, not FitOps Pro. Stripe only lets that app, or you, change it.`
        : opts.appName === null
          ? 'This was made in your own Stripe account, not through FitOps Pro. Stripe only lets you change it.'
          : 'Some of your clients are on subscriptions and invoices that were made outside FitOps Pro, for example by GoHighLevel. Stripe only lets the app that made them, or you, change them.';
  /** @type {HTMLAnchorElement} */ (el('stripe-key-open')).href = STRIPE_KEYS_URL[mode];
  el('stripe-key-prefix').textContent = `rk_${mode}_`;
  const input = /** @type {HTMLInputElement} */ (el('stripe-key-input'));
  input.placeholder = `rk_${mode}_…`;
  input.value = '';
  showError('');

  const saveBtn = /** @type {HTMLButtonElement} */ (el('stripe-key-save'));
  saveBtn.disabled = true;
  saveBtn.textContent = 'Save';
  saveBtn.onclick = async () => {
    const key = input.value.trim();
    if (!key) return;
    saveBtn.disabled = true;
    saveBtn.textContent = 'Checking with Stripe…';
    try {
      await opts.save(key);
      close(true);
    } catch (err) {
      const e = /** @type {any} */ (err);
      showError(e && e.status === 403
        ? 'Only someone who can manage billing can add this key. Ask the account owner to add it in Settings → Payments.'
        : (e && e.message) || 'Something went wrong. Try again.');
      saveBtn.disabled = !input.value.trim();
    } finally {
      saveBtn.textContent = 'Save';
    }
  };

  /** @type {HTMLElement} */ (overlay).classList.remove('hidden');
  input.focus();
  return new Promise((resolve) => { settle = resolve; });
}
